/**
 * GRBMapModal — links a property to an Account via GRB ID.
 *
 * Used by CX / CX-Leader / Admin in /properties. Opens with the property's
 * current grb_property_id pre-filled. The user can either:
 *   1. Search and pick an account from the searchable dropdown
 *      (suggestions show "Account Name (GRB ID)"), OR
 *   2. Manually type a GRB ID directly into the input.
 *
 * On Save, calls:
 *   PUT /api/properties/{id}  body: { grb_property_id, leadId? }
 * The backend's existing master-form mapping logic (role_insights._master_form_mapped_set)
 * picks up the new value, so the GTM / CX / Leader Insights "Master Form"
 * counter auto-updates on next refresh.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Loader2, X, Search, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const GRBMapModal = ({ property, onClose, onSaved }) => {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [grbId, setGrbId] = useState(property?.grb_property_id || '');
  const [linkedLeadId, setLinkedLeadId] = useState(property?.leadId || null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchTimer = useRef();

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchAccounts = useCallback(async (q) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: '50' });
      if (q) qs.set('search', q);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads?${qs.toString()}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setAccounts(Array.isArray(body) ? body : (body.data || []));
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAccounts(''); }, [fetchAccounts]);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchAccounts(search), 250);
    return () => clearTimeout(searchTimer.current);
  }, [search, fetchAccounts]);

  const pickAccount = (acct) => {
    setGrbId(acct.grb_property_id || '');
    setLinkedLeadId(acct.id || null);
  };

  const handleSave = async () => {
    if (!grbId.trim()) { toast.error('Please enter or pick a GRB ID'); return; }
    setSaving(true);
    try {
      const payload = { grb_property_id: grbId.trim() };
      // Best-effort: also set leadId so future calls can hop both ways.
      if (linkedLeadId) payload.leadId = linkedLeadId;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/properties/${property.id}`,
        { method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success('Property mapped to account');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save mapping');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="grb-map-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" style={{ maxHeight: '80vh' }}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-teal-600" />
            <div>
              <h3 className="font-bold text-gray-800 text-sm">Map property to account</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">{property?.propertyName || property?.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded" data-testid="grb-map-close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">GRB ID</label>
            <input
              value={grbId}
              onChange={(e) => { setGrbId(e.target.value); setLinkedLeadId(null); }}
              placeholder="e.g. GRB1234"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="grb-map-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">Type directly or pick from the suggestions below.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Or pick an account</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by hotel name or GRB ID…"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="grb-map-account-search"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="grb-map-account-list">
              {loading ? (
                <div className="flex items-center justify-center py-6 text-gray-400"><Loader2 size={16} className="animate-spin" /></div>
              ) : accounts.length === 0 ? (
                <p className="text-[11px] text-gray-400 text-center py-6">No accounts match.</p>
              ) : (
                accounts.map(a => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => pickAccount(a)}
                    className={`w-full text-left px-3 py-2 hover:bg-teal-50 text-sm ${
                      linkedLeadId === a.id ? 'bg-teal-50' : ''
                    }`}
                    data-testid={`grb-map-account-${a.id}`}
                  >
                    <div className="font-medium text-gray-800 truncate">{a.hotel_name || '(no name)'}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-2">
                      <span className="font-mono">{a.grb_property_id || 'no GRB'}</span>
                      {a.lead_owner && <span>· {a.lead_owner}</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-[11px] text-teal-800">
            Saving this mapping marks the account's Master Form as complete across all role-based Insights.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} type="button" className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-white">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !grbId.trim()}
            type="button"
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="grb-map-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            Save mapping
          </button>
        </div>
      </div>
    </div>
  );
};

export default GRBMapModal;
